(function () {
  'use strict';

  var instances = new WeakMap();
  var motionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function prefersReducedMotion() {
    return motionQuery && motionQuery.matches;
  }

  function hasFinePointer() {
    return window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function parseColor(value) {
    var hex = (value || '').trim().replace('#', '');
    if (hex.length === 3) hex = hex.split('').map(function (digit) { return digit + digit; }).join('');
    if (!/^[0-9a-f]{6}$/i.test(hex)) return [247, 243, 233];
    return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
  }

  function colorBetween(first, second, progress) {
    return first.map(function (channel, index) {
      return Math.round(channel + (second[index] - channel) * progress);
    });
  }

  function colorString(rgb) {
    return 'rgb(' + rgb.join(', ') + ')';
  }

  function init(section) {
    if (!section || instances.has(section)) return;

    var cards = Array.prototype.slice.call(section.querySelectorAll('[data-why-card]'));
    var cleanups = [];
    var frame = 0;
    var motionReduced = prefersReducedMotion();
    var finePointer = hasFinePointer();
    var pointerEnabled = section.dataset.motionEnabled === 'true' && !motionReduced && finePointer;
    var scrollCardsEnabled = section.dataset.motionEnabled === 'true' && !motionReduced && !finePointer;
    var motionStrength = clamp(Number(section.dataset.motionStrength || 100) / 100, 0, 1);
    var scrollEnabled = section.dataset.scrollColorEnabled === 'true' && !motionReduced;
    var startColor = parseColor(section.style.getPropertyValue('--why-bg-start'));
    var middleColor = parseColor(section.style.getPropertyValue('--why-bg-middle'));
    var endColor = parseColor(section.style.getPropertyValue('--why-bg-end'));

    if (!motionReduced) section.classList.add('is-enhanced');

    if (window.IntersectionObserver && !motionReduced) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      }, { threshold: 0.12 });
      cards.forEach(function (card) { observer.observe(card); });
      cleanups.push(function () { observer.disconnect(); });
    } else {
      cards.forEach(function (card) { card.classList.add('is-visible'); });
    }

    function resetCards() {
      cards.forEach(function (card) {
        var tilt = card.querySelector('.why-it-fails__card-tilt');
        if (!tilt) return;
        tilt.style.setProperty('--why-rotate-y', '0deg');
        tilt.style.setProperty('--why-rotate-x', '0deg');
        tilt.style.setProperty('--why-x', '0px');
        tilt.style.setProperty('--why-y', '0px');
        tilt.style.setProperty('--why-scroll-rotate', '0deg');
      });
    }

    if (pointerEnabled) {
      var move = function (event) {
        var bounds = section.getBoundingClientRect();
        var x = (event.clientX - bounds.left) / bounds.width - 0.5;
        var y = (event.clientY - bounds.top) / bounds.height - 0.5;
        cards.forEach(function (card, index) {
          var tilt = card.querySelector('.why-it-fails__card-tilt');
          if (!tilt) return;
          var depth = [0.76, 0.9, 1.06, 1.2][index] || 1;
          var applied = (0.7 + motionStrength * 0.3) * depth;
          tilt.style.setProperty('--why-rotate-y', (x * 8 * applied).toFixed(2) + 'deg');
          tilt.style.setProperty('--why-rotate-x', (-y * 8 * applied).toFixed(2) + 'deg');
          tilt.style.setProperty('--why-x', (x * 24 * applied).toFixed(1) + 'px');
          tilt.style.setProperty('--why-y', (y * 24 * applied).toFixed(1) + 'px');
        });
      };
      section.addEventListener('pointermove', move, { passive: true });
      section.addEventListener('pointerleave', resetCards);
      cleanups.push(function () {
        section.removeEventListener('pointermove', move);
        section.removeEventListener('pointerleave', resetCards);
      });
    }

    function updateScrollState() {
      frame = 0;
      var bounds = section.getBoundingClientRect();
      if (scrollCardsEnabled) {
        var viewportCentre = window.innerHeight / 2;
        cards.forEach(function (card, index) {
          var tilt = card.querySelector('.why-it-fails__card-tilt');
          if (!tilt) return;
          var cardCentre = card.getBoundingClientRect().top + card.offsetHeight / 2;
          var distance = clamp((cardCentre - viewportCentre) / (window.innerHeight * 0.75), -1, 1);
          var depth = [0.76, 0.9, 1.06, 1.2][index] || 1;
          tilt.style.setProperty('--why-y', (-distance * 8 * depth).toFixed(1) + 'px');
          tilt.style.setProperty('--why-scroll-rotate', (distance * 1.5 * depth).toFixed(2) + 'deg');
        });
      }
      if (scrollEnabled) {
        var progress = clamp((window.innerHeight - bounds.top) / (window.innerHeight + bounds.height), 0, 1);
        var color = progress <= 0.5
          ? colorBetween(startColor, middleColor, progress * 2)
          : colorBetween(middleColor, endColor, (progress - 0.5) * 2);
        section.style.setProperty('--why-bg-current', colorString(color));
      }
    }

    function requestScrollColor() {
      if (!frame) frame = window.requestAnimationFrame(updateScrollState);
    }

    if (scrollEnabled || scrollCardsEnabled) {
      updateScrollState();
      window.addEventListener('scroll', requestScrollColor, { passive: true });
      window.addEventListener('resize', requestScrollColor);
      cleanups.push(function () {
        if (frame) window.cancelAnimationFrame(frame);
        window.removeEventListener('scroll', requestScrollColor);
        window.removeEventListener('resize', requestScrollColor);
        section.style.removeProperty('--why-bg-current');
      });
    }

    var cleanup = function () {
      cleanups.forEach(function (remove) { remove(); });
      section.classList.remove('is-enhanced');
      instances.delete(section);
    };
    instances.set(section, cleanup);
  }

  function destroy(root) {
    var section = root && root.matches && root.matches('[data-why-it-fails]') ? root : root && root.querySelector('[data-why-it-fails]');
    var cleanup = section && instances.get(section);
    if (cleanup) cleanup();
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-why-it-fails]').forEach(init);
  }

  function handleMotionPreferenceChange() {
    document.querySelectorAll('[data-why-it-fails]').forEach(function (section) {
      var cleanup = instances.get(section);
      if (cleanup) cleanup();
      init(section);
    });
  }

  document.addEventListener('DOMContentLoaded', function () { initAll(); });
  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
  document.addEventListener('shopify:section:unload', function (event) { destroy(event.target); });
  document.addEventListener('shopify:section:reorder', function () { initAll(); });
  if (motionQuery) {
    if (motionQuery.addEventListener) motionQuery.addEventListener('change', handleMotionPreferenceChange);
    else if (motionQuery.addListener) motionQuery.addListener(handleMotionPreferenceChange);
  }
}());
